import type { AnchorHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { pickCss } from '@/shared/lib/css-utils';
import css from './nav-item.module.scss';

const styles = pickCss(css, css.root);

type CommonProps = {
  icon?: ReactNode;
  active?: boolean;
  className?: string;
  children?: ReactNode;
};

// When `href` is provided the item renders as a real `<a>` so the browser
// offers "Open in new tab", middle-click, and Cmd/Ctrl+click natively. Without
// it, the item stays a `<div>` driven by `onClick` (legacy button-style usage).
type AnchorNavItemProps = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof CommonProps> & {
    href: string;
  };

type DivNavItemProps = CommonProps &
  Omit<HTMLAttributes<HTMLDivElement>, keyof CommonProps> & {
    href?: undefined;
  };

type NavItemProps = AnchorNavItemProps | DivNavItemProps;

export const NavItem = ({
  icon,
  active = false,
  className,
  children,
  ...restProps
}: NavItemProps) => {
  const content = (
    <>
      {icon && <span className={css.icon}>{icon}</span>}
      <span className={css.label}>{children}</span>
    </>
  );
  const cls = styles({ isActive: active }, className);

  if (restProps.href !== undefined) {
    return (
      <a
        className={cls}
        {...(restProps as AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {content}
      </a>
    );
  }

  return (
    <div className={cls} {...(restProps as HTMLAttributes<HTMLDivElement>)}>
      {content}
    </div>
  );
};
